/**
 * Build-time feature gates.
 *
 * The former VITE_SHOW_* env-var overrides (deferred pages, preferences,
 * security, send-contract, excluded dates) were never provisioned in any
 * build, so their default branches are now the only behaviour: deferred
 * pages / preferences / security are admin-only, "Send Contract" is hidden
 * everywhere, and excluded commission dates show everywhere (since #2236).
 */

/**
 * #1024 — "Excluded Commission Dates" on the hotel affiliations page.
 *
 * Shown everywhere since the production cutover of 2026-10-26 (#2236): the
 * commission engine reads exclusions from the platform rules in production,
 * and the Airtable form that used to feed them no longer reaches the platform,
 * so this is the only way to enter or end an exclusion.
 *
 * Before the cutover it was hidden on production hosts because nothing
 * enforced the saved rules there. The function stays (and every surface still
 * calls it — see exclusionsGate.rotguard.test.ts) so the UI can be re-gated in
 * one place if that ever becomes necessary again.
 */
export function showExcludedCommissionDates(): boolean {
  return true
}

/**
 * True on a local development host. Kept separate from isStagingEnv() so the
 * two read independently — a flag that wants "everywhere except production"
 * must say so explicitly rather than relying on staging detection to cover dev.
 */
export function isLocalEnv(): boolean {
  return (
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.test(window.location.host)
  )
}

/**
 * True when the app is running on a staging host (e.g. `app.staging.hoteliate.com`).
 *
 * Detection is purely host-based — matches a `staging.` label anywhere in the
 * host — so production (e.g. `app.hoteliate.com`) is automatically unaffected
 * with NO env/secret changes. Used to gate staging-only UI hides (#663/#669/#670).
 */
export function isStagingEnv(): boolean {
  return typeof window !== 'undefined' && /(^|\.)staging\./.test(window.location.host)
}
